/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { inject, Injectable } from '@angular/core';
import { AppStore } from './../../store/AppStore';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { Auth as FirebaseAuth, Auth } from '@angular/fire/auth';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, firstValueFrom, of } from 'rxjs';
import { AuthorizationService } from '../authorization/authorization.service';
import { IAuthorization } from '@lib/definitions/sidebar-item.definition';
import { IRefreshCookieResponse } from '@lib/interfaces/auth-cookie.interface';
import { AnalyticsService } from '../analytics/analytics.service';
import { LoginService } from '@modules/auth/presentation/lib/services/login.service';
/**
 * Servicio que contiene la lógica de autenticación con OAuth2
 */
@Injectable({ providedIn: 'root' })
export class OAuthService {
  static signOut() {
    throw new Error('Method not implemented.');
  }
  private appStore = inject(AppStore);
  private analytics = inject(AnalyticsService);
  private isLoggingOut = false;
  authInstance!: Auth;
  refreshBody: any

  constructor(
    private router: Router,
    private authFirebase: FirebaseAuth,
    private http: HttpClient,
    private authorization: AuthorizationService,
    readonly loginService: LoginService
  ) { }

  private getReturnURL() {
    return window.location.hash
      ? window.location.href.replace(window.location.hash, '')
      : window.location.href;
  }

  async azureAuth(email?: string) {
    const obj = {
      'Transaction-id': '1',
      Secuence: '1',
      endUser: email!
    };
    const headers: HttpHeaders = new HttpHeaders(obj);
    try {
      // Comprobamos si devuelve 401
      const firstStep = await firstValueFrom(
        this.http.post(environment.proxyOIDC, { headers }).pipe(
          catchError((el) => {
            return of(el.status === 401);
          })
        )
      );
      // Consultamos la URL de login del IDP
      if (firstStep) {
        const url = `${environment.proxyBaseUrl}/oidc-okta-1/login?from=${this.getReturnURL()}&apikey=${environment.clientId}`;
        window.location.href = url;
      }
      this.router.navigateByUrl('/home');
    } catch (error) {
      console.error('Error durante la autenticación con Azure:', error);
      // Manejo de errores
    }
  }

  async fetchIdCookie(forceRefresh: boolean = false): Promise<string | null> {
    if (!this.authFirebase.currentUser) {
      return null;
    }

    try {
      const idCookie =
        await this.authFirebase.currentUser.getIdToken(forceRefresh);
      return idCookie;
    } catch (error) {
      console.error('Error al obtener el ID token:', error);
      return null; // Propaga el error para manejarlo donde sea necesario
    }
  }

  setUserData(user: IAuthorization) {
    this.appStore.setUserSession({ ...user });
  }

  async validateUserSession() {
    try {
      const sessionData = JSON.parse(localStorage.getItem('userSession')!);
      if (!sessionData) {
        return false;
      }
      await firstValueFrom(
        this.http.post(
          `${environment.proxyBaseUrl}/portalweb-oauth2-cc-pg-protected/protected`,
          sessionData
        )
      );
      this.setUserData(sessionData);
      return true;
    } catch {
      localStorage.clear();
      return false;
    }
  }

  async refreshCookie() {
    await this.getNewAccessCookie({
    });
    const userInfo: IAuthorization = JSON.parse(
      localStorage.getItem('userSession')!
    );
    userInfo.loginDate = new Date().getTime();

    localStorage.setItem('userSession', JSON.stringify(userInfo));

    this.appStore.setUserSession({ ...userInfo });
  }

  async getNewAccessCookie(paramsData: {
    [key: string]: string;
  }): Promise<IRefreshCookieResponse> {
    let headers = new HttpHeaders();
    Object.keys(paramsData).forEach((key) => {
      headers = headers.set(key, paramsData[key]);
    });

    const cookie = await firstValueFrom(
      this.http.post<IRefreshCookieResponse>(
        `${environment.proxyBaseUrl}/apigee/portal-managment/refresh-token`,
        {},
        {
          headers,
        }
      )
    );
    return cookie;
  }

  async logout(_omitRefresh = false) {
    const userInfo = this.appStore.userInfo();

    if (this.isLoggingOut || (!userInfo?.email && !localStorage.getItem('userSession'))) {
      return;
    }

    this.isLoggingOut = true;
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.proxyBaseUrl}/apigee/portal-managment-session-killer`,
          {},
          { withCredentials: true }
        )
      );
      localStorage.clear();
      sessionStorage.clear();
      this.appStore.signOut();
      this.loginService.state = 'USER_VALIDATION';
      await this.router.navigate(['/auth/login']);
    } catch (error) {
      console.error('Session killer API error:', error);
    } finally {
      this.isLoggingOut = false;
    }
  }

  // Verifica si el usuario está autenticado
  isAuthenticated(): boolean {
    return !!this.appStore.userInfo()?.email;
  }
}
